/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react'
import {
   DemandAnalysis,
   DemandAnalysisQuestion,
   DemandAnalysisQuestionActionPlanResponse,
   DemandAnalysisQuestionStatus,
   ActionPlanStatus,
} from '../../../api/schemas/schema'
import { Divider, RadioGroup, Radio, Button, makeStyles, tokens } from '@fluentui/react-components'
import { ArrowReset20Regular } from '@fluentui/react-icons'
import { ModalResetAnswer } from './ModalResetAnswer'
import { Text } from '../../common/text/Text'
import { ControlledTextInput } from '../../common/inputs/TextInput'
import { useForm } from 'react-hook-form'
import { ModalActions } from '../../common/modal/Modal'
import { api } from '../../../helpers/apiHelper'
import { DataContext } from '../../../context/DataContext'
import { handleMessage } from '../../../helpers/stateHelper'
import { Field } from '../../common/inputs/Field'
import { RTFOutput } from '../../common/outputs/RTFOutput'
import { t } from '../../../i18n/i18n'
import { ControlledMoneyInput } from '../../common/inputs/ControlledMoneyInput'
import { InformationPopovers } from './popovers/DemandInformationPopovers'
import { IItemState } from '../../../interfaces/IItemState'
import { DemandAnalysisQuestionPopovers } from './popovers/DemandAnalysisQuestionPopovers'
import { DraggableModal } from '../../common/modal/DraggableModal'
import { Form } from '../../common/form/Form'

const useStyles = makeStyles({
   formWrapper: {
      marginTop: tokens.spacingVerticalM,
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalL,
   },
   textWrapper: {
      '> p:last-child': {
         marginTop: tokens.spacingVerticalXS,
      },
      '> p:first-child': {
         marginBottom: 0,
      },
   },
   inputWrapper: {
      '& > :not(:first-child)': {
         marginTop: tokens.spacingVerticalM,
      },
   },
   popoversContainer: {
      justifyContent: 'space-around',
   },
   radioButtonContainer: {
      display: 'flex',
      justifyContent: 'space-between',
   },
   rightActions: {
      display: 'flex',
      gap: tokens.spacingHorizontalL,
      '@media screen and (max-width: 640px)': {
         flexDirection: 'column-reverse',
         gap: tokens.spacingHorizontalS,
      },
   },
})

type ModalAnalysisQuestionProps = {
   item: DemandAnalysisQuestion
   close: () => void
   setItems: React.Dispatch<React.SetStateAction<DemandAnalysisQuestion[]>>
   setAnalysisState: React.Dispatch<React.SetStateAction<IItemState<DemandAnalysis>>>
   analysisDisabled: boolean
   questionActionPlanStatus?: ActionPlanStatus
}

export const ModalAnalysisQuestion = ({ item, close, setItems, setAnalysisState, analysisDisabled, questionActionPlanStatus }: ModalAnalysisQuestionProps) => {
   const dataContext = useContext(DataContext)
   const { userAccount, organisation } = dataContext.state
   const classes = useStyles()
   const [confirmResetOpen, setConfirmResetOpen] = useState(false)
   const [resetting, setResetting] = useState(false)
   const {
      control,
      register,
      setValue,
      watch,
      reset,
      handleSubmit,
      formState: { isDirty, isSubmitting, errors },
   } = useForm<DemandAnalysisQuestion>({
      disabled: analysisDisabled,
      defaultValues: { answerId: undefined, answer: undefined },
   })

   useEffect(() => {
      reset(item)
   }, [item])


   const isClosed = questionActionPlanStatus === ActionPlanStatus.Decided || questionActionPlanStatus === ActionPlanStatus.NotToBeAdressed

   const onSubmit = async (data: DemandAnalysisQuestion) => {
      try {
         if (!data.answer.actionRequired) setValue('proposedAction', null)
         if (!data.answeredById) {
            data.answeredById = userAccount.id
            data.answeredBy = userAccount
         }
         data.modifiedBy = userAccount
         data.modifiedById = userAccount.id
         const response: DemandAnalysisQuestionActionPlanResponse = (await api().createActionPlanForDemandAnalysisQuestion(organisation.id, data)).data
         const updatedQuestion = response.updatedQuestion
         const createdActionPlan = response.actionPlan

         setItems((prev) => {
            const updatedItems = [...prev]
            updatedItems[updatedItems.findIndex((x) => x.id === updatedQuestion.id)] = updatedQuestion
            return updatedItems
         })

         setAnalysisState((prev) => {
            const updatedItems = [...prev.item.questions]
            updatedItems[updatedItems.findIndex((x) => x.id === updatedQuestion.id)] = updatedQuestion

            const updatedActionPlans = createdActionPlan ? [...prev.item.actionPlans, createdActionPlan] : prev.item.actionPlans

            return { ...prev, item: { ...prev.item, questions: updatedItems, actionPlans: updatedActionPlans } }
         })

         handleMessage(dataContext.setRootState, 'success', t('QuestionAnswered'))
         reset()
         close()
      } catch (error: any) {
         handleMessage(dataContext.setRootState, 'error', t('CouldNotAnswerQuestion'))
      }
   }

   const resetAnswer = async () => {
      setResetting(true)
      try {
         const data: DemandAnalysisQuestion = { ...item, modifiedBy: userAccount, modifiedById: userAccount.id }
         const response: DemandAnalysisQuestionActionPlanResponse = (await api().resetAnswerForDemandAnalysisQuestion(organisation.id, data)).data
         const updatedQuestion = response.updatedQuestion

         setItems((prev) => {
            const updatedItems = [...prev]
            updatedItems[updatedItems.findIndex((x) => x.id === updatedQuestion.id)] = updatedQuestion
            return updatedItems
         })

         setAnalysisState((prev) => {
            const updatedQuestions = [...prev.item.questions]
            updatedQuestions[updatedQuestions.findIndex((x) => x.id === updatedQuestion.id)] = updatedQuestion
            const updatedActionPlans = prev.item.actionPlans.filter((x) => x.demandAnalysisQuestionId !== updatedQuestion.id)

            return { ...prev, item: { ...prev.item, questions: updatedQuestions, actionPlans: updatedActionPlans } }
         })

         handleMessage(dataContext.setRootState, 'success', t('AnswerReset'))
         setConfirmResetOpen(false)
         reset()
         close()
      } catch (error: any) {
         handleMessage(dataContext.setRootState, 'error', t('CouldNotResetAnswer'))
         setConfirmResetOpen(false)
      } finally {
         setResetting(false)
      }
   }

   register('answer', {
      validate: (a) => a !== null || t('RequiredField'),
   })

   const closeAndReset = () => {
      reset()
      close()
   }

   return (
      <>
         <DraggableModal title={t('ComplianceOfDemands')} open={item ? true : false} onClose={closeAndReset}>
            {item ? (
               <Form onSubmit={handleSubmit(onSubmit)}>
                  <InformationPopovers className={classes.popoversContainer} item={item} />

                  <div className={classes.textWrapper}>
                     <Text weight="bold">
                        {t('Question')} {item.number}
                     </Text>
                     <RTFOutput text={item.text} />
                  </div>
                  <Divider appearance="brand" />
                  <section className={classes.formWrapper}>
                     <div className={classes.radioButtonContainer}>
                        <Field
                           label={t('SpecifyCompliance')}
                           required
                           validationState={errors.answer ? 'error' : undefined}
                           validationMessage={errors.answer?.message}
                        >
                           <RadioGroup
                              disabled={isClosed || analysisDisabled}
                              value={watch('answerId') ?? ''}
                              onChange={(e, data) => {
                                 setValue('answerId', data.value)
                                 setValue(
                                    'answer',
                                    item.answerOptions.find((x) => x.id === data.value),
                                    { shouldDirty: true }
                                 )
                              }}
                           >
                              {item.answerOptions && item.answerOptions.map((o) => <Radio value={o.id} label={o.text} />)}
                           </RadioGroup>
                        </Field>
                        <DemandAnalysisQuestionPopovers {...item} />
                     </div>
                     {watch('answer') && watch('answer').actionRequired ? (
                        <div className={classes.inputWrapper}>
                           <ControlledTextInput
                              disabled={isClosed}
                              label={t('ProposedAction')}
                              control={control}
                              name={'proposedAction'}
                              multiline
                              rows={2}
                              required
                           />
                           <ControlledMoneyInput disabled={isClosed} label={t('EstimatedCost')} name={'estimatedCost'} control={control} />
                        </div>
                     ) : null}
                     <ControlledTextInput disabled={isClosed} label={t('Comment')} control={control} name={'comment'} multiline rows={2} />
                     <ModalActions>
                        <Button onClick={closeAndReset} disabled={isSubmitting}>
                           {!isClosed && !analysisDisabled ? t('Cancel') : t('Close')}
                        </Button>
                        <span className={classes.rightActions}>
                           <Button
                              appearance="secondary"
                              icon={<ArrowReset20Regular />}
                              onClick={() => setConfirmResetOpen(true)}
                              disabled={item.status === DemandAnalysisQuestionStatus.NotAnswered || analysisDisabled}
                           >
                              {t('ResetAnswer')}
                           </Button>
                           {!isClosed && !analysisDisabled && (
                              <Button type="submit" appearance="primary" disabled={!isDirty || isSubmitting}>
                                 {t('Save')}
                              </Button>
                           )}
                        </span>
                     </ModalActions>
                  </section>
               </Form>
            ) : null}
         </DraggableModal>
         <ModalResetAnswer open={confirmResetOpen} close={() => setConfirmResetOpen(false)} onConfirm={resetAnswer} isSubmitting={resetting} />
      </>
   )
}
